import moment from 'moment';

/**
 * Minimal FHIR Observation fields used for active-chip batching.
 *
 * Every member carries an explicit `| undefined` so that a real
 * `fhir/r4` `Observation` — whose optional fields are declared exactly that way
 * — is structurally assignable here under `exactOptionalPropertyTypes`. Without
 * it, `observations.tsx` cannot pass the `ResourceEntry<'Observation'>[]` it
 * actually holds, despite the runtime shapes being identical.
 */
type ObservationResource = {
  id?: string | undefined;
  meta?: { lastUpdated?: string | undefined } | undefined;
  derivedFrom?: Array<{ display?: string | undefined; reference?: string | undefined }> | undefined;
  effectivePeriod?: { start?: string | undefined } | undefined;
};

/** FHIR bundle entry shape used when resolving active profile observations. */
export type ObservationBundleEntry = {
  resource?: ObservationResource;
};

/**
 * Returns observation ids that render as active (teal) chips in the profile UI.
 *
 * @remarks
 * Matches the batching rules in `observations.tsx`: same questionnaire response within 15 seconds,
 * or `effectivePeriod.start` values within 15 seconds of the first row in the list.
 *
 * @param data - Observation entries for a single preference category (e.g. communication preferences).
 *
 * @remarks
 * SUSPECTED DEFECT — `data` is declared required but `observations.tsx` passes
 * its optional `props.data`, which is absent until the profile query resolves.
 * The `data[0]` read below then throws on `undefined` rather than returning
 * `[]`. The parameter states the absence so the mismatch is visible at the call
 * site; the `data[0]` read is left as written, since returning `[]` for an
 * absent argument would newly swallow that failure.
 */
export function getHighlightedObservationIds(data: ObservationBundleEntry[] | undefined): string[] {
  if (!data![0]) {
    return [];
  }

  const highlightIds: string[] = [];
  let lastDataInfo = '';
  let lastUpdatedDate = '';

  for (let i = 0; i < data!.length; i++) {
    const resource = data![i]?.resource;
    let currentDataInfo = '';

    const derivedFrom = resource?.derivedFrom;
    if (derivedFrom && derivedFrom.length > 0) {
      if (i === 0) {
        lastUpdatedDate = resource.meta?.lastUpdated ?? '';
      }
      const indexOfQuestionnaire = derivedFrom.findIndex((obj) => (obj.display = 'Questionnaire'));
      currentDataInfo = derivedFrom[indexOfQuestionnaire]?.reference?.split('/')?.[1] ?? '';
    } else {
      currentDataInfo = resource?.effectivePeriod?.start ?? '';
    }

    if (!lastDataInfo) {
      lastDataInfo = currentDataInfo;
    }

    const isHighlighted =
      (currentDataInfo === lastDataInfo &&
        moment(lastUpdatedDate).diff(resource?.meta?.lastUpdated, 'seconds') < 15) ||
      moment(lastDataInfo).diff(currentDataInfo, 'seconds') < 15;

    const observationId = resource?.id;
    if (isHighlighted && observationId && !highlightIds.includes(observationId)) {
      highlightIds.push(observationId);
    }
  }

  return highlightIds;
}
